import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Save, Upload, Eye, Edit3, Home, Settings, FileText, Users, MessageSquare } from 'lucide-react';

const AdminPanel = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [content, setContent] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load content from mock data
  useEffect(() => {
    // In a real app, this would load from your backend
    // For now, we'll use the mock data structure
    setContent({
      hero: {
        headline: "Virtual Nurse Support for Families Caring for Aging Loved Ones",
        subheadline: "We help families navigate care, coordinate services, and feel confident they're making the right decisions — so you don't have to figure it out alone.",
        primaryCta: "Talk to a Nurse",
        secondaryCta: "How It Works"
      },
      contact: {
        email: "info@nursebloc.io",
        headline: "Schedule a Free Nurse Consult",
        description: "Tell us a little about your situation"
      },
      services: [
        {
          title: "Medication Management",
          description: "Expert guidance organizing complex medication schedules, preventing dangerous interactions, and tracking side effects."
        },
        {
          title: "Healthcare Navigation", 
          description: "Navigate the fragmented healthcare system with confidence. We coordinate between multiple specialists."
        },
        {
          title: "Medical Translation & Advocacy",
          description: "Transform complex medical language into clear, actionable decisions."
        }
      ],
      testimonials: [
        {
          quote: "Managing 5+ medications with different schedules is overwhelming and scary.",
          author: "Sarah M.",
          relation: "Daughter coordinating care for mother"
        },
        {
          quote: "Doctors don't talk to each other, and no one is coordinating the big picture.",
          author: "Michael R.",
          relation: "Son coordinating 4 specialists for father"
        }
      ]
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    // Simulate saving
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }, 1000);
  };

  const updateContent = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateService = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === index ? { ...service, [field]: value } : service
      )
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((testimonial, i) => 
        i === index ? { ...testimonial, [field]: value } : testimonial
      )
    }));
  };

  const menuItems = [
    { id: 'hero', label: 'Hero Section', icon: Home },
    { id: 'contact', label: 'Contact Info', icon: MessageSquare },
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'testimonials', label: 'Testimonials', icon: Users }
  ];

  if (previewMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Preview Mode</h1>
            <Button onClick={() => setPreviewMode(false)} variant="outline">
              <Edit3 className="w-4 h-4 mr-2" />
              Back to Editor
            </Button>
          </div>
        </div>
        <div className="p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{content.hero?.headline}</h2>
            <p className="text-xl text-gray-600 mb-6">{content.hero?.subheadline}</p>
            <div className="flex gap-4">
              <Button className="btn-nursebloc">{content.hero?.primaryCta}</Button>
              <Button variant="outline">{content.hero?.secondaryCta}</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-violet-600" />
              <h1 className="text-xl font-bold text-gray-800">NurseBloc Admin Panel</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setPreviewMode(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <>Saving...</>
                ) : saved ? (
                  <>✓ Saved</>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeSection === item.id
                        ? 'bg-violet-100 text-violet-700 border-l-4 border-violet-600'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {activeSection === 'hero' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Hero Section</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Main Headline</label>
                    <Input
                      value={content.hero?.headline || ''}
                      onChange={(e) => updateContent('hero', 'headline', e.target.value)}
                      placeholder="Enter main headline"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subheadline</label>
                    <Textarea
                      value={content.hero?.subheadline || ''}
                      onChange={(e) => updateContent('hero', 'subheadline', e.target.value)}
                      placeholder="Enter subheadline"
                      rows={3}
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Button Text</label>
                      <Input
                        value={content.hero?.primaryCta || ''}
                        onChange={(e) => updateContent('hero', 'primaryCta', e.target.value)}
                        placeholder="Primary CTA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Button Text</label>
                      <Input
                        value={content.hero?.secondaryCta || ''}
                        onChange={(e) => updateContent('hero', 'secondaryCta', e.target.value)}
                        placeholder="Secondary CTA"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'contact' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <Input
                      value={content.contact?.email || ''}
                      onChange={(e) => updateContent('contact', 'email', e.target.value)}
                      placeholder="info@nursebloc.io"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Form Headline</label>
                    <Input
                      value={content.contact?.headline || ''}
                      onChange={(e) => updateContent('contact', 'headline', e.target.value)}
                      placeholder="Schedule a Free Nurse Consult"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Form Description</label>
                    <Textarea
                      value={content.contact?.description || ''}
                      onChange={(e) => updateContent('contact', 'description', e.target.value)}
                      placeholder="Tell us a little about your situation"
                      rows={2}
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {activeSection === 'services' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Services</h2>
                  
                  {content.services?.map((service, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-6 space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Service {index + 1}</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Title</label>
                        <Input
                          value={service.title || ''}
                          onChange={(e) => updateService(index, 'title', e.target.value)}
                          placeholder="Service title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Description</label>
                        <Textarea
                          value={service.description || ''}
                          onChange={(e) => updateService(index, 'description', e.target.value)}
                          placeholder="Service description"
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeSection === 'testimonials' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Testimonials</h2>
                  
                  {content.testimonials?.map((testimonial, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-6 space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Testimonial {index + 1}</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quote</label>
                        <Textarea
                          value={testimonial.quote || ''}
                          onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                          placeholder="Customer quote"
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Author Name</label>
                          <Input
                            value={testimonial.author || ''}
                            onChange={(e) => updateTestimonial(index, 'author', e.target.value)}
                            placeholder="Author name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                          <Input
                            value={testimonial.relation || ''}
                            onChange={(e) => updateTestimonial(index, 'relation', e.target.value)}
                            placeholder="e.g., Daughter, Son, Wife"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
